import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import TextField from '@/common/components/TextField'
import { zodResolver } from '@hookform/resolvers/zod'
import { ChangeEvent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import useDebounce from '@/common/hooks/useDebounce'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { FormTransformers } from '@/common/utils/formTransformers'
import useRedeemRewardItem from '../hooks/useRedeemRewardItem'
import { redeemRewardItemSchema, RedeemRewardItemValidationSchema } from '../schemas/redeem.schema'
import { useFindRewardItemByCode } from '../hooks/useFindRewardItemByCode'
import RewardItemPreview from './RewardItemPreview'

export const RedeemRewardItemModal = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<boolean | string[]>(false)
  const [code, setCode] = useDebounce('');

  const {
    register,
    setError,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<RedeemRewardItemValidationSchema>({
    resolver: zodResolver(redeemRewardItemSchema),
  })

  const { 
    data: rewardItem, 
    isError: rewardItemIsError,
    isFetching: rewardItemIsLoading,
    error: rewardItemError,
  } = useFindRewardItemByCode({
    filters: {
      code
    },
  })
  
  const {
    mutate: redeemRewardItem,
    isPending: redeemRewardItemIsPending,
    isError: redeemRewardItemIsError
  } = useRedeemRewardItem({
    id: rewardItem?.id,
    onSuccess: () => {
      setIsOpen(false)
      reset()
      setCode('')
      setMessages(['Item resgatado com sucesso!']);
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setMessages, setError })(error)
      }
    }
  })

  const codeIsCompleted = code.length === 11
  const confirmButtonIsDisabled = 
    !codeIsCompleted 
    || rewardItemIsError 
    || redeemRewardItemIsPending 
    || rewardItemIsLoading
    || !rewardItem

  const { onChange: hookFormOnChange, ...restCodeRegister } = register('code')

  useEffect(() => {
    reset()
    setCode('')
  }, [isOpen])

  const onSubmit = () => redeemRewardItem();

  return (
    <>
      <Modal
        Trigger={
          <Button 
            variant='link-white'
            className='text-blue-800 hover:text-blue-700 px-6'
            operationKlassToCheckPermission="Admin::Tmais::RewardProductRedemptions::FindByCode"
          >
            Resgatar recompensa
          </Button>
        }
        title="Resgatar recompensa"
        subtitle="Insira o código do produto para resgate."
        subtitleClassName="mt-1 text-white-300"
        useCancellableFooter
        confirmButtonText='Confirmar'
        confirmButtonClassName='bg-primary-blue hover:bg-primary-blue-hover disabled:opacity-50 disabled:hover:bg-primary-blue'
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        disabled={confirmButtonIsDisabled}
        onConfirm={handleSubmit(onSubmit)}
        body={
          <div className="flex flex-col gap-2 text-white-800">
            <TextField
              label="Código de resgate"
              placeholder="Informe o código"
              markAsRequired
              {...restCodeRegister}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                hookFormOnChange(e)
                setCode(e.target?.value)
              }}
              errorMessage={errors.code?.message}
              variant={errors.code?.message ? 'error' : 'default'}
              maxLength={11}
            />
            {codeIsCompleted && (
              <RewardItemPreview
                rewardItem={rewardItem?.product}
                rewardItemIsLoading={rewardItemIsLoading}
                rewardItemError={rewardItemError}
              />
            )}
          </div>
        }
      />
      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={rewardItemIsError || redeemRewardItemIsError}
        />
      )}
    </>
  )
}
